import React, { useEffect, useRef, useState } from "react";

import type { MeetingStatus } from "@performyard/models";
import { ButtonStandard, Checkbox, Text } from "@performyard/py-components";

import ContentModal from "../../../../../../components/Modal/Templates/ContentModal";

interface StatusConfirmationModalProps {
	isOpen: boolean;
	isLoading: boolean;
	status: MeetingStatus | null;
	action?: "status-change" | "remove-from-calendar";
	onClose: () => void;
	onConfirm: (removeFromCalendar?: boolean) => void;
	// Calendar integration props - only used for cancel status with connected calendar
	hasConnectedCalendarEvent?: boolean;
}

export const StatusConfirmationModal = ({
	isOpen,
	isLoading,
	status,
	action = "status-change",
	onClose,
	onConfirm,
	hasConnectedCalendarEvent = false,
}: StatusConfirmationModalProps) => {
	const [removeFromCalendar, setRemoveFromCalendar] = useState(false);

	// Capture content when modal is open to persist during closing animation
	const contentRef = useRef<{
		config: { title: string; message: string; confirmText: string; confirmButtonColor: "green" | "red" } | null;
		showCheckbox: boolean;
	}>({ config: null, showCheckbox: false });

	const getActionConfig = (action: "status-change" | "remove-from-calendar", status: MeetingStatus | null) => {
		if (action === "remove-from-calendar") {
			return {
				title: "Remove Meeting From Calendar?",
				message:
					"Do you want to remove this meeting from your calendar? This will delete the event, but you can always add a new one later.",
				confirmText: "Yes, Remove",
				confirmButtonColor: "red" as const,
			};
		}

		switch (status) {
			case "canceled":
				return {
					title: "Cancel Meeting?",
					message:
						"All meeting notes, agenda, and action items will be placed in read-only mode and no longer configurable.",
					confirmText: "Yes, Cancel Meeting",
					confirmButtonColor: "green" as const,
				};
			case "completed":
				return {
					title: "Complete Meeting?",
					message: "Are you sure you want to complete this meeting?",
					confirmText: "Yes, Complete Meeting",
					confirmButtonColor: "green" as const,
				};
			case "archived":
				return {
					title: "Archive Meeting?",
					message:
						"All meeting notes, agenda, and action items will be placed in read-only mode and no longer configurable.",
					confirmText: "Yes, Archive Meeting",
					confirmButtonColor: "green" as const,
				};
			default:
				return null;
		}
	};

	// Update content ref when modal is open and has valid content
	useEffect(() => {
		if (isOpen) {
			const config = getActionConfig(action, status);
			// Only show checkbox for "Cancel Meeting" status change
			const showCheckbox = action === "status-change" && status === "canceled" && hasConnectedCalendarEvent;
			contentRef.current = { config, showCheckbox };
		}
	}, [isOpen, action, status, hasConnectedCalendarEvent]);

	// Use current content if modal is open, otherwise use captured content for closing animation
	const config = isOpen ? getActionConfig(action, status) : contentRef.current.config;
	// Only show checkbox for "Cancel Meeting" status change
	const showCalendarCheckbox = isOpen
		? action === "status-change" && status === "canceled" && hasConnectedCalendarEvent
		: contentRef.current.showCheckbox;

	const handleConfirm = () => {
		onConfirm(removeFromCalendar);
	};

	const handleClose = () => {
		setRemoveFromCalendar(false); // Reset checkbox state on close
		onClose();
	};

	return (
		<ContentModal
			isOpen={isOpen}
			handleClose={isLoading ? () => {} : handleClose}
			size="large"
			title={
				<Text size="sub-heading" weight="semi-bold">
					{config?.title}
				</Text>
			}
			buttons={[
				<div key="cancel">
					<ButtonStandard
						variant="solid"
						color="primary"
						size="large"
						onClick={handleClose}
						disabled={isLoading}
					>
						Cancel
					</ButtonStandard>
				</div>,
				<div key="confirm">
					<ButtonStandard
						variant="solid"
						color={config?.confirmButtonColor}
						size="large"
						onClick={handleConfirm}
						disabled={isLoading}
					>
						{config?.confirmText}
					</ButtonStandard>
				</div>,
			]}
		>
			<div className="space-y-4 text-center">
				<Text size="paragraph-1" weight="regular">
					{config?.message}
				</Text>
				{showCalendarCheckbox && (
					<div className="flex items-center justify-center space-x-2">
						<Checkbox
							id="remove-from-calendar"
							checked={removeFromCalendar}
							onCheckedChange={setRemoveFromCalendar}
							disabled={isLoading}
							aria-label="Remove from My Calendar"
						/>
						<Text size="paragraph-3" weight="regular">
							Remove from My Calendar
						</Text>
					</div>
				)}
			</div>
		</ContentModal>
	);
};
